import { useCallback, useEffect, useRef, useState } from "react";
import {
  getAuditableUrls,
  type AuditableUrlGroups,
} from "@/services/auditable-urls";

interface UseAuditableUrlsResult {
  data: AuditableUrlGroups | null;
  isLoading: boolean;
  error: Error | null;
  refetch: (force?: boolean) => Promise<void>;
}

export function useAuditableUrls(): UseAuditableUrlsResult {
  const [data, setData] = useState<AuditableUrlGroups | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchData = useCallback(async (force = false) => {
    if (isMountedRef.current) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const result = await getAuditableUrls(force);
      if (isMountedRef.current) setData(result);
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
